import { getNestedErrorMessage } from '@/utils/utils';
import PropTypes from 'prop-types';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ValidationErrorMessage from '@/components/error/validation-error-component';
import Styled from './radio-button-style';

const defaultProps = {
  registerOptions: {},
  label: '',
  disabled: false,
  radioContainerStyle: {},
  mainContainerStyle: {},
  hideLabel: false,
};

const propTypes = {
  /** Array of options to be rendered */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /** Name of the radio button */
  name: PropTypes.string.isRequired,
  /** Register options for React Hook Form */
  registerOptions: PropTypes.shape({}),
  /** The label of the input */
  label: PropTypes.string,
  /** The disabled state of the input */
  disabled: PropTypes.bool,
  /** Style for the radio button */
  radioContainerStyle: PropTypes.shape({}),
  /** Style for the radio button Container */
  mainContainerStyle: PropTypes.shape({}),
  control: PropTypes.shape({}).isRequired,
  formState: PropTypes.shape({
    errors: PropTypes.shape({}),
    isLoading: PropTypes.bool,
  }).isRequired,
  /** Hide the label for the radio button */
  hideLabel: PropTypes.bool,
};

const RadioButton = ({ field, option, radioContainerStyle }) => (
  <Styled.RadioContainer style={radioContainerStyle}>
    <Styled.Radio
      {...field}
      type="radio"
      key={option.value}
      value={option.value}
      checked={field.value === option.value}
    />
    <Styled.ButtonText>{option.label}</Styled.ButtonText>
  </Styled.RadioContainer>
);

RadioButton.propTypes = {
  option: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  field: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  radioContainerStyle: PropTypes.shape({}).isRequired,
};

RadioButton.defaultProps = {
  field: {
    value: '',
    label: '',
  },
};

/**
 * Radio button group used in forms
 * @param {object} props - Component props
 * @param {Array} props.options - Array of options to be rendered
 * @param {string} props.name - Name of the radio button
 * @param {string} props.registerOptions - Register options for React Hook Form
 * @param {string} props.label - Label for the radio button
 * @param {string} props.hideLabel - Hide the label for the radio button
 * @param {boolean} props.disabled - The disabled state of the input
 * @param {object} props.mainContainerStyle - Style for the main container of radio button
 * @param {object} props.radioContainerStyle - Style for the radio button
 * @param {object} props.control - React Hook Form control
 * @param {object} props.formState - React Hook Form form state
 * @returns {React.Component}
 */
export const RawRadioButtonGroup = ({
  options,
  name,
  registerOptions,
  label,
  disabled,
  mainContainerStyle,
  radioContainerStyle,
  control,
  formState,
  hideLabel,
}) => {
  const { errors } = formState;

  const errorMessage = getNestedErrorMessage(name, errors);

  const getLabel = () => {
    if (hideLabel) {
      return null;
    }
    return label ? <Styled.Label>{label}</Styled.Label> : <Styled.NoLabel />;
  };

  return (
    <>
      {!hideLabel && getLabel()}
      <Styled.Flex style={mainContainerStyle}>
        <Controller
          control={control}
          name={name}
          rules={registerOptions}
          disabled={disabled}
          render={({ field }) =>
            options.map((option, idx) => (
              <RadioButton
                key={`radio___button__${idx + 1}`}
                field={field}
                option={option}
                radioContainerStyle={radioContainerStyle}
              />
            ))
          }
        />
      </Styled.Flex>
      {errorMessage && (
        <Styled.ErrorDiv>
          <ValidationErrorMessage message={errorMessage} />
        </Styled.ErrorDiv>
      )}
    </>
  );
};

RawRadioButtonGroup.propTypes = propTypes;

RawRadioButtonGroup.defaultProps = defaultProps;

/**
 * The Select Search component
 * @param {object} props - props
 * @returns {JSXElement} - SelectList
 */
const RadioButtonGroup = (props) => {
  const { control, formState } = useFormContext();

  return (
    <RawRadioButtonGroup {...props} control={control} formState={formState} />
  );
};
RadioButtonGroup.defaultProps = defaultProps;

RadioButtonGroup.propTypes = propTypes;

export default RadioButtonGroup;
